import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import RiskIssueManagement from './RiskIssueManagement';
import SROICalculator from './SROICalculator';
import ActivityFeed from './ActivityFeed';
import TeamManagement from './TeamManagement';
import StakeholderManagement from './StakeholderManagement';
// import DocumentManagement from './DocumentManagement';
import AdvancedReporting from './AdvancedReporting';
import GanttChart from './GanttChart';
import FinancialManagement from './FinancialManagement';
import FundingLedger from './FundingLedger';
import SocialActorAuditLog from './SocialActorAuditLog';
import BeneficiaryTracking from './BeneficiaryTracking';
import ComplianceAudit from './ComplianceAudit';
import VideoVerification from './VideoVerification';
import MobileFieldApp from './MobileFieldApp';
import NotificationCenter from './NotificationCenter';
import QualityAssuranceModule from './QualityAssuranceModule';
import AdvancedAnalyticsAI from './AdvancedAnalyticsAI';
import AnalyticsDashboard from './AnalyticsDashboard';
import SmartAlerts from './SmartAlerts';
import ErrorBoundary from './ErrorBoundary';
import AdvancedSearch from './AdvancedSearch';
import ProcurementManagement from './ProcurementManagement';
import DigitalChecklists from './DigitalChecklists';
import DocumentEvidenceManagement from './DocumentEvidenceManagement';
import UniversalComments from './UniversalComments';
import ContractualCompliance from './ContractualCompliance';
import TemplateLibrary from './TemplateLibrary';
import { toast } from 'sonner';
import SidebarUserFooter from './SidebarUserFooter';
import { 
  ArrowLeft,
  Plus,
  Calendar,
  DollarSign,
  Target,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  BookOpen,
  Heart,
  Leaf,
  Activity,
  Users,
  LogOut,
  Menu,
  X,
  BarChart3,
  Package,
  Award,
  AlertTriangle,
  Bug,
  Calculator,
  Upload,
  FileText,
  Download,
  X as XIcon,
  Edit3,
  Trash2,
  Layers
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const ProjectDetails = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [provenance, setProvenance] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showKpiDialog, setShowKpiDialog] = useState(false);
  const [showDeliverableDialog, setShowDeliverableDialog] = useState(false);
  const [kpiFormData, setKpiFormData] = useState({
    name: '',
    type: '',
    baseline_value: '',
    target_value: '',
    actual_value: '',
    unit: ''
  });
  const [editingKpi, setEditingKpi] = useState(null);
  const [deliverableFormData, setDeliverableFormData] = useState({
    name: '',
    description: '',
    due_date: '',
    files: []
  });
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  
  // Approval workflow states
  const [showKpiApprovalDialog, setShowKpiApprovalDialog] = useState(false);
  const [showDeliverableApprovalDialog, setShowDeliverableApprovalDialog] = useState(false);
  const [approvingKpi, setApprovingKpi] = useState(null);
  const [approvingDeliverable, setApprovingDeliverable] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Bulk selection states
  const [selectedKpis, setSelectedKpis] = useState([]);
  const [selectedDeliverables, setSelectedDeliverables] = useState([]);
  const [showBulkApprovalDialog, setShowBulkApprovalDialog] = useState(false);
  const [bulkApprovalType, setBulkApprovalType] = useState(''); // 'kpis' or 'deliverables'
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      const [projectRes, kpisRes, deliverablesRes, originRes, provenanceRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/kpis`),
        axios.get(`${API}/projects/${id}/deliverables`),
        axios.get(`${API}/projects/${id}/origin`).catch(() => ({ data: null })),
        axios.get(`${API}/blended-investments/project-provenance/${id}`).catch(() => ({ data: null })),
      ]);

      setProject(projectRes.data);
      setKpis(kpisRes.data);
      setDeliverables(deliverablesRes.data);
      setOrigin(originRes.data && originRes.data.bi_plan_id ? originRes.data : null);
      setProvenance(provenanceRes.data && provenanceRes.data.bi_plan_id ? provenanceRes.data : null);
    } catch (error) {
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKpi = async (e) => {
    e.preventDefault();
    try {
      const isEditing = editingKpi !== null;
      const kpiData = {
        ...kpiFormData,
        baseline_value: parseFloat(kpiFormData.baseline_value),
        target_value: parseFloat(kpiFormData.target_value),
      };
      // Only include actual_value when it's an edit and the field is non-empty
      // (create endpoint doesn't accept it; backend default 0.0 applies)
      if (isEditing && kpiFormData.actual_value !== '' && kpiFormData.actual_value !== null && kpiFormData.actual_value !== undefined) {
        kpiData.actual_value = parseFloat(kpiFormData.actual_value);
      } else {
        delete kpiData.actual_value;
      }

      if (isEditing) {
        await axios.put(`${API}/projects/${id}/kpis/${editingKpi}`, kpiData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('KPI updated successfully!');
      } else {
        await axios.post(`${API}/projects/${id}/kpis`, kpiData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('KPI created successfully!');
      }
      
      setShowKpiDialog(false);
      setKpiFormData({
        name: '',
        description: '',
        baseline_value: '',
        target_value: '',
        actual_value: '',
        unit: ''
      });
      setEditingKpi(null);
      fetchProjectDetails();
    } catch (error) {
      console.error('KPI Error details:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      let errorMsg = 'Failed to update KPI';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          // Standard FastAPI error
          if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation error array
            errorMsg = error.response.data.detail.map(err => err.msg).join(', ');
          } else {
            errorMsg = error.response.data.detail;
          }
        } else if (Array.isArray(error.response.data)) {
          // Direct Pydantic validation error array
          errorMsg = error.response.data.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    if (!window.confirm('Are you sure you want to delete this KPI?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${id}/kpis/${kpiId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('KPI deleted successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Failed to delete KPI:', error);
      toast.error('Failed to delete KPI');
    }
  };

  // KPI Approval Handler
  const handleKpiApproval = async (approved) => {
    try {
      await axios.post(
        `${API}/projects/${id}/kpis/${approvingKpi.id}/approve-edit`,
        { approved, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(approved ? 'KPI edit approved!' : 'KPI edit rejected');
      setShowKpiApprovalDialog(false);
      setApprovingKpi(null);
      setApprovalNotes('');
      fetchProjectDetails();
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error('Failed to process approval');
    }
  };
  
  // Deliverable Approval Handler
  const handleDeliverableApproval = async (approved) => {
    try {
      await axios.post(
        `${API}/projects/${id}/deliverables/${approvingDeliverable.id}/approve-edit`,
        { approved, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(approved ? 'Deliverable edit approved!' : 'Deliverable edit rejected');
      setShowDeliverableApprovalDialog(false);
      setApprovingDeliverable(null);
      setApprovalNotes('');
      fetchProjectDetails();
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error('Failed to process approval');
    }
  };

  // Bulk Approval Handler
  const handleBulkApproval = async (approved) => {
    try {
      const selectedItems = bulkApprovalType === 'kpis' ? selectedKpis : selectedDeliverables;
      
      if (selectedItems.length === 0) {
        toast.error('No items selected');
        return;
      }
      
      const items = selectedItems.map(itemId => ({
        id: itemId,
        approved: approved,
        notes: bulkApprovalNotes
      }));
      
      const response = await axios.post(
        `${API}/projects/${id}/bulk-approve-edits`,
        {
          entity_type: bulkApprovalType,
          items: items,
          common_notes: bulkApprovalNotes
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(
        `Bulk ${approved ? 'approval' : 'rejection'} complete! ` +
        `${response.data.successful} successful, ${response.data.failed} failed`
      );
      
      setShowBulkApprovalDialog(false);
      setBulkApprovalNotes('');
      setSelectedKpis([]);
      setSelectedDeliverables([]);
      fetchProjectDetails();
    } catch (error) {
      console.error('Failed to process bulk approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk approval');
    }
  };
  
  // Toggle selection
  const toggleKpiSelection = (kpiId) => {
    setSelectedKpis(prev => 
      prev.includes(kpiId) ? prev.filter(id => id !== kpiId) : [...prev, kpiId]
    );
  };
  
  const toggleDeliverableSelection = (deliverableId) => {
    setSelectedDeliverables(prev => 
      prev.includes(deliverableId) ? prev.filter(id => id !== deliverableId) : [...prev, deliverableId]
    );
  };

  // PDF Report Generation Handler
  const handleGeneratePDFReport = async (reportType = 'comprehensive') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/projects/${id}/reports/generate-pdf?report_type=${reportType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project_report_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report generated successfully!');
    } catch (error) {
      console.error('Failed to generate PDF report:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  const handleCreateDeliverable = async (e) => {
    e.preventDefault();
    try {
      const deliverableData = {
        ...deliverableFormData,
        due_date: new Date(deliverableFormData.due_date).toISOString(),
        files: [] // Will be populated after file uploads
      };

      let deliverableId;
      let isEditing = editingDeliverable !== null;

      if (isEditing) {
        // Update existing deliverable
        await axios.put(`${API}/projects/${id}/deliverables/${editingDeliverable}`, deliverableData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        deliverableId = editingDeliverable;
      } else {
        // Create new deliverable
        const response = await axios.post(`${API}/projects/${id}/deliverables`, deliverableData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        deliverableId = response.data.id;
      }

      // Upload files if any
      if (deliverableFormData.files.length > 0) {
        const uploadPromises = deliverableFormData.files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('deliverable_id', deliverableId);
          
          return axios.post(`${API}/projects/${id}/deliverables/${deliverableId}/upload`, formData, {
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        });

        await Promise.all(uploadPromises);
      }

      toast.success(`Deliverable ${isEditing ? 'updated' : 'created'} successfully! ${deliverableFormData.files.length > 0 ? `${deliverableFormData.files.length} file(s) uploaded.` : ''}`);
      setShowDeliverableDialog(false);
      setDeliverableFormData({
        name: '',
        description: '',
        due_date: '',
        files: []
      });
      setEditingDeliverable(null);
      fetchProjectDetails();
    } catch (error) {
      console.error(`Failed to ${editingDeliverable ? 'update' : 'create'} deliverable:`, error);
      
      let errorMsg = `Failed to ${editingDeliverable ? 'update' : 'create'} deliverable`;
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMsg = error.response.data.detail.map(err => err.msg).join(', ');
          } else {
            errorMsg = error.response.data.detail;
          }
        } else if (Array.isArray(error.response.data)) {
          errorMsg = error.response.data.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    }
  };

  const handleDeleteDeliverable = async (deliverableId) => {
    if (!window.confirm('Are you sure you want to delete this deliverable?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${id}/deliverables/${deliverableId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Deliverable deleted successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Failed to delete deliverable:', error);
      toast.error('Failed to delete deliverable');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'education': return <BookOpen className="h-5 w-5" />;
      case 'care': return <Heart className="h-5 w-5" />;
      case 'environment': return <Leaf className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'education': return 'bg-blue-100 text-blue-800';
      case 'care': return 'bg-green-100 text-green-800';
      case 'environment': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'executing': return 'bg-green-100 text-green-800';
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'initiating': return 'bg-blue-100 text-blue-800';
      case 'closing': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDeliverableStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'certified': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'planned': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateKpiProgress = (kpi) => {
    const range = kpi.target_value - kpi.baseline_value;
    const progress = kpi.actual_value - kpi.baseline_value;
    return Math.max(0, Math.min(100, (progress / range) * 100));
  };

  // Build a numeric series from a KPI's actual_history. Series starts at
  // the first recorded "from" value and walks through each "to". When no
  // history exists, we still draw a flat baseline→actual trend so every
  // card has a visual.
  const buildKpiSeries = (kpi) => {
    const hist = Array.isArray(kpi.actual_history) ? kpi.actual_history : [];
    if (hist.length === 0) {
      return [Number(kpi.baseline_value) || 0, Number(kpi.actual_value) || 0];
    }
    const series = [Number(hist[0].from) || 0];
    hist.forEach((h) => series.push(Number(h.to) || 0));
    return series;
  };

  // Compact inline SVG sparkline (no external deps). Renders the series
  // as a smoothed line + filled area + end dot. Width fixed; height 30.
  const KPISparkline = ({ series, target }) => {
    if (!series || series.length < 2) return null;
    const W = 120;
    const H = 30;
    const pad = 2;
    const min = Math.min(...series, target ?? series[0]);
    const max = Math.max(...series, target ?? series[0]);
    const range = max - min || 1;
    const stepX = (W - pad * 2) / (series.length - 1);
    const points = series.map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (H - pad * 2) * (1 - (v - min) / range);
      return { x, y, v };
    });
    const last = points[points.length - 1];
    const first = points[0];
    const up = last.v >= first.v;
    const stroke = up ? '#10b981' : '#ef4444';
    const fill = up ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)';
    const polyline = points.map((p) => `${p.x},${p.y}`).join(' ');
    const area = `M ${first.x},${H} L ${points.map((p) => `${p.x},${p.y}`).join(' L ')} L ${last.x},${H} Z`;
    const targetY = target != null
      ? pad + (H - pad * 2) * (1 - (target - min) / range)
      : null;
    return (
      <svg width={W} height={H} className="inline-block align-middle" aria-label="actual-value sparkline">
        <path d={area} fill={fill} />
        {targetY != null && (
          <line x1={pad} x2={W - pad} y1={targetY} y2={targetY}
                stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="2 2" />
        )}
        <polyline points={polyline} fill="none" stroke={stroke} strokeWidth="1.5"
                  strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last.x} cy={last.y} r="2.5" fill={stroke} />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('project_detail.not_found_title')}</h2>
          <p className="text-gray-600 mb-4">{t('project_detail.not_found_desc')}</p>
          <Link to="/projects">
            <Button>{t('project_detail.back_to_projects')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">ISISP Platform</h2>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Social Impact Management</p>
        </div>

        <nav className="mt-6 px-4">
          <div className="space-y-2">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <BarChart3 className="h-5 w-5" />
              <span>Dashboard</span>
            </Link>
            <Link 
              to="/projects" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <Activity className="h-5 w-5" />
              <span>Projects</span>
            </Link>
            <Link 
              to="/templates" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              <span>Electronic Library of Financial Instruments & Supporting Material</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-4 right-4">
          <SidebarUserFooter user={user} onLogout={onLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link 
                to="/projects" 
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                data-testid="back-to-projects"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('project_detail.back_to_projects')}</span>
              </Link>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Project Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <div className="p-2 rounded-full bg-gray-100">
                    {getCategoryIcon(project.category)}
                  </div>
                  <Badge className={getCategoryColor(project.category)}>
                    {project.category}
                  </Badge>
                  <Badge className={getStatusColor(project.status)}>
                    {project.status.replace('_', ' ')}
                  </Badge>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="project-title">
                  {project.title}
                </h1>
                <p className="text-gray-600 max-w-3xl" data-testid="project-description">
                  {project.description}
                </p>
                {origin?.bi_plan_id && (
                  <div
                    className="mt-3 rounded-lg bg-gradient-to-r from-indigo-50 via-emerald-50 to-white border border-indigo-200 p-4"
                    data-testid="project-from-blended-banner"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-indigo-700 mb-1">
                          <Layers className="h-3.5 w-3.5" />
                          {t('provenance.eyebrow', 'Provenance')}
                        </div>
                        <p className="text-sm text-slate-800 font-semibold">
                          {t('provenance.title', 'Originated from Blended Plan')}
                          {origin.bi_plan_title ? <>: <span className="text-indigo-700">{origin.bi_plan_title}</span></> : null}
                        </p>
                        {provenance && (provenance.segments_total > 0) && (
                          <p className="text-xs text-slate-500 mt-0.5">{provenance.segments_total} {t('provenance.segments', 'segments')}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 underline"
                        onClick={() => navigate(`/combined-investments/${origin.bi_plan_id}`)}
                        data-testid="back-to-blended-plan"
                      >
                        {t('provenance.view_plan', 'View source plan')} →
                      </button>
                    </div>

                    {provenance && (provenance.seeded?.kpis || provenance.seeded?.beneficiaries || provenance.seeded?.risks || provenance.seeded?.stakeholders || provenance.seeded?.budget_categories) ? (
                      <div className="mt-3 pt-3 border-t border-indigo-100">
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                          {t('provenance.seeded', 'Auto-seeded at conversion:')}
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {provenance.seeded?.kpis > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200" data-testid="prov-kpis">
                              <span className="font-bold text-emerald-700">{provenance.seeded.kpis}</span>
                              <span className="text-slate-600">{t('provenance.kpis', 'KPIs')}</span>
                            </span>
                          )}
                          {provenance.seeded?.beneficiaries > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200" data-testid="prov-beneficiaries">
                              <span className="font-bold text-emerald-700">{provenance.seeded.beneficiaries}</span>
                              <span className="text-slate-600">{t('provenance.beneficiaries', 'beneficiaries')}</span>
                            </span>
                          )}
                          {provenance.seeded?.risks > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200" data-testid="prov-risks">
                              <span className="font-bold text-emerald-700">{provenance.seeded.risks}</span>
                              <span className="text-slate-600">{t('provenance.risks', 'risks')}</span>
                            </span>
                          )}
                          {provenance.seeded?.stakeholders > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200" data-testid="prov-stakeholders">
                              <span className="font-bold text-emerald-700">{provenance.seeded.stakeholders}</span>
                              <span className="text-slate-600">{t('provenance.stakeholders', 'stakeholders')}</span>
                            </span>
                          )}
                          {provenance.seeded?.budget_categories > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200" data-testid="prov-budget">
                              <span className="font-bold text-emerald-700">{provenance.seeded.budget_categories}</span>
                              <span className="text-slate-600">{t('provenance.budget', 'budget lines')}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3"
                       title={
                         project.progress_breakdown
                           ? Object.entries(project.progress_breakdown)
                               .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}%`)
                               .join(' · ')
                           : 'Auto-computed from deliverables and KPIs'
                       }
                       data-testid="progress-tile">
                    <div className="p-2 rounded-full bg-blue-100">
                      <Target className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('project_detail.stat_progress')}</p>
                      <p className="text-2xl font-bold text-blue-600">{Math.round(project.progress)}%</p>
                      {project.progress_breakdown && (
                        <p className="text-xs text-gray-500 leading-tight" data-testid="progress-breakdown">
                          {Object.entries(project.progress_breakdown)
                            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)} ${v}%`)
                            .join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-green-100">
                      <DollarSign className="h-5 w-5 text-green-700" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('project_detail.stat_budget')}</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(project.budget)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-orange-100">
                      <TrendingUp className="h-5 w-5 text-orange-700" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('project_detail.stat_spent')}</p>
                      <p className="text-lg font-bold text-orange-700">{formatCurrency(project.actual_cost)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-purple-100">
                      <Calendar className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('project_detail.stat_end_date')}</p>
                      <p className="text-sm font-bold text-purple-600">{new Date(project.end_date).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="flex flex-wrap w-full bg-muted p-1 text-muted-foreground" style={{height: 'auto', flexDirection: 'row', justifyContent: 'flex-start'}}>
              {/* All tabs with specific width to force wrapping after 10 tabs */}
              <TabsTrigger value="kpis" data-testid="kpis-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_kpis')}</TabsTrigger>
              <TabsTrigger value="deliverables" data-testid="deliverables-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_deliverables')}</TabsTrigger>
              <TabsTrigger value="risks" data-testid="risks-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_risks')}</TabsTrigger>
              <TabsTrigger value="impact" data-testid="impact-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_impact')}</TabsTrigger>
              {/* Documents tab removed - functionality moved to deliverables */}
              <TabsTrigger value="timeline" data-testid="timeline-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_timeline')}</TabsTrigger>
              <TabsTrigger value="financial" data-testid="financial-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_financial')}</TabsTrigger>
              <TabsTrigger value="funding" data-testid="funding-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>Funding</TabsTrigger>
              {(user?.role === 'administrator' || user?.role === 'project_manager' || user?.role === 'social_actor') && (
                <TabsTrigger value="audit" data-testid="audit-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>Audit</TabsTrigger>
              )}
              <TabsTrigger value="beneficiaries" data-testid="beneficiaries-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_beneficiaries')}</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="compliance-tab" className="text-xs" style={{width: '9.8%', margin: '1px'}}>{t('project_detail.tab_compliance')}</TabsTrigger>
              
              {/* Force line break and second row */}
              <div style={{flexBasis: '100%', height: '0'}}></div>
              
              <TabsTrigger value="videos" data-testid="videos-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_videos')}</TabsTrigger>
              <TabsTrigger value="mobile" data-testid="mobile-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_mobile')}</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="notifications-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_notifications')}</TabsTrigger>
              <TabsTrigger value="quality" data-testid="quality-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_quality')}</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="analytics-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_analytics')}</TabsTrigger>
              <TabsTrigger value="procurement" data-testid="procurement-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_procurement')}</TabsTrigger>
              <TabsTrigger value="reports" data-testid="reports-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_reports')}</TabsTrigger>
              <TabsTrigger value="activity" data-testid="activity-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_activity')}</TabsTrigger>
              <TabsTrigger value="collaboration" data-testid="collaboration-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_collaboration')}</TabsTrigger>
              <TabsTrigger value="checklists" data-testid="checklists-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_checklists')}</TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab" className="text-xs" style={{width: '9.5%', margin: '1px'}}>{t('project_detail.tab_documents')}</TabsTrigger>
            </TabsList>

            {/* KPIs Tab */}
            <TabsContent value="kpis" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">{t('project_forms.kpis_section_title')}</h2>
                <Dialog open={showKpiDialog} onOpenChange={setShowKpiDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-kpi-button">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('project_forms.add_kpi_btn')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingKpi ? t('project_forms.kpi_edit_title') : t('project_forms.kpi_add_title')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateKpi} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="kpi-name">{t('project_forms.kpi_name')}</Label>
                        <Input
                          id="kpi-name"
                          value={kpiFormData.name}
                          onChange={(e) => setKpiFormData({...kpiFormData, name: e.target.value})}
                          placeholder={t('project_forms.kpi_name_placeholder')}
                          required
                          data-testid="kpi-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="kpi-type">{t('project_forms.kpi_type')}</Label>
                          <Select value={kpiFormData.type} onValueChange={(value) => setKpiFormData({...kpiFormData, type: value})}>
                            <SelectTrigger data-testid="kpi-type-select" className="relative z-50">
                              <SelectValue placeholder={t('project_forms.kpi_select_type')} />
                            </SelectTrigger>
                            <SelectContent className="z-50">
                              <SelectItem value="quantitative">{t('project_forms.kpi_type_quantitative')}</SelectItem>
                              <SelectItem value="qualitative">{t('project_forms.kpi_type_qualitative')}</SelectItem>
                              <SelectItem value="impact">{t('project_forms.kpi_type_impact')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="kpi-unit">{t('project_forms.kpi_unit')}</Label>
                          <Input
                            id="kpi-unit"
                            value={kpiFormData.unit}
                            onChange={(e) => setKpiFormData({...kpiFormData, unit: e.target.value})}
                            placeholder={t('project_forms.kpi_unit_placeholder')}
                            required
                            data-testid="kpi-unit-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="baseline">{t('project_forms.kpi_baseline')}</Label>
                          <Input
                            id="baseline"
                            type="number"
                            value={kpiFormData.baseline_value}
                            onChange={(e) => setKpiFormData({...kpiFormData, baseline_value: e.target.value})}
                            placeholder="0"
                            required
                            data-testid="kpi-baseline-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target">{t('project_forms.kpi_target')}</Label>
                          <Input
                            id="target"
                            type="number"
                            value={kpiFormData.target_value}
                            onChange={(e) => setKpiFormData({...kpiFormData, target_value: e.target.value})}
                            placeholder="100"
                            required
                            data-testid="kpi-target-input"
                          />
                        </div>
                      </div>
                      {editingKpi && (
                        <div className="space-y-2">
                          <Label htmlFor="actual">Actual value</Label>
                          <Input
                            id="actual"
                            type="number"
                            value={kpiFormData.actual_value}
                            onChange={(e) => setKpiFormData({...kpiFormData, actual_value: e.target.value})}
                            placeholder="Current actual value"
                            data-testid="kpi-actual-input"
                          />
                          <p className="text-xs text-gray-500">
                            Every update is recorded in the KPI history (visible on the card). Social Actor edits route through PM/Admin approval.
                          </p>
                        </div>
                      )}
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowKpiDialog(false);
                          setEditingKpi(null);
                          setKpiFormData({
                            name: '',
                            description: '',
                            baseline_value: '',
                            target_value: '',
                            actual_value: '',
                            unit: ''
                          });
                        }}>
                          {t('project_forms.cancel')}
                        </Button>
                        <Button type="submit" data-testid="create-kpi-submit">{editingKpi ? t('project_forms.kpi_update_btn') : t('project_forms.kpi_create_btn')}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>


              {/* Bulk Actions Toolbar for KPIs */}
              {(user.role === 'project_manager' || user.role === 'administrator') && 
               kpis.some(kpi => kpi.edit_status === 'pending_approval') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <p className="text-sm font-medium text-blue-900">
                        {t('project_forms.kpi_pending_count', { count: kpis.filter(k => k.edit_status === 'pending_approval').length })}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const pendingKpis = kpis.filter(k => k.edit_status === 'pending_approval').map(k => k.id);
                          setSelectedKpis(pendingKpis);
                        }}
                      >
                        {t('project_forms.select_all_pending')}
                      </Button>
                      {selectedKpis.length > 0 && (
                        <span className="text-sm text-blue-700">
                          {t('project_forms.count_selected', { count: selectedKpis.length })}
                        </span>
                      )}
                    </div>
                    {selectedKpis.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkApprovalType('kpis');
                            setShowBulkApprovalDialog(true);
                          }}
                          className="bg-green-700 hover:bg-green-800"
                        >
                          {t('project_forms.bulk_approve_label', { count: selectedKpis.length })}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setBulkApprovalType('kpis');
                            setShowBulkApprovalDialog(true);
                          }}
                        >
                          {t('project_forms.bulk_reject_label', { count: selectedKpis.length })}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedKpis([])}
                        >
                          {t('project_forms.clear_btn')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {kpis.map((kpi) => (
                  <Card key={kpi.id} data-testid={`kpi-card-${kpi.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-2">
                          {/* Checkbox for bulk selection - only for pending approval items */}
                          {(user.role === 'project_manager' || user.role === 'administrator') && 
                           kpi.edit_status === 'pending_approval' && (
                            <input
                              type="checkbox"
                              checked={selectedKpis.includes(kpi.id)}
                              onChange={() => toggleKpiSelection(kpi.id)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                          )}
                          <CardTitle className="text-lg">{kpi.name}</CardTitle>
                          {/* Approval Status Badge */}
                          {kpi.edit_status === 'pending_approval' && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                              {t('project_forms.pending_approval_badge')}
                            </Badge>
                          )}
                          {kpi.edit_status === 'rejected' && (
                            <Badge variant="destructive">
                              {t('project_forms.edit_rejected_badge')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="capitalize">
                            {kpi.type}
                          </Badge>
                          {/* Approval Button for PM/Admin */}
                          {(user.role === 'project_manager' || user.role === 'administrator') && 
                           kpi.edit_status === 'pending_approval' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setApprovingKpi(kpi);
                                setShowKpiApprovalDialog(true);
                              }}
                              className="text-green-700 hover:text-green-800"
                            >
                              {t('project_forms.review_btn')}
                            </Button>
                          )}
                          {/* Edit/Delete Actions for Admin and Social Actor */}
                          {user.role !== 'investor' && (
                            <div className="flex items-center space-x-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setKpiFormData({
                                    name: kpi.name,
                                    description: kpi.description,
                                    type: kpi.type || '',
                                    baseline_value: kpi.baseline_value.toString(),
                                    target_value: kpi.target_value.toString(),
                                    actual_value: (kpi.actual_value ?? '').toString(),
                                    unit: kpi.unit
                                  });
                                  setEditingKpi(kpi.id);
                                  setShowKpiDialog(true);
                                }}
                                className="text-blue-700 hover:text-blue-800"
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteKpi(kpi.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">{t('project_forms.baseline_label')}</p>
                            <p className="font-semibold">{kpi.baseline_value} {kpi.unit}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">{t('project_forms.target_label')}</p>
                            <p className="font-semibold">{kpi.target_value} {kpi.unit}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">{t('project_forms.actual_label')}</p>
                            <p className="font-semibold">{kpi.actual_value} {kpi.unit}</p>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{t('project_forms.progress_label')}</span>
                            <div className="flex items-center gap-2">
                              <KPISparkline series={buildKpiSeries(kpi)} target={kpi.target_value} />
                              <span className="text-sm text-gray-600 tabular-nums">
                                {Math.round(calculateKpiProgress(kpi))}%
                              </span>
                            </div>
                          </div>
                          <Progress value={calculateKpiProgress(kpi)} />
                        </div>

                        {/* Actual-value history timeline — append-only audit
                            of every change to `actual_value`. Visible to all
                            roles so the project team can see how progress
                            evolved. */}
                        {Array.isArray(kpi.actual_history) && kpi.actual_history.length > 0 && (
                          <details className="mt-3 group" data-testid={`kpi-history-${kpi.id}`}>
                            <summary className="text-xs font-medium text-blue-700 cursor-pointer hover:underline">
                              Actual history ({kpi.actual_history.length} change{kpi.actual_history.length === 1 ? '' : 's'})
                            </summary>
                            <ul className="mt-2 space-y-1.5 pl-1 border-l-2 border-blue-100">
                              {[...kpi.actual_history].reverse().map((h, idx) => (
                                <li key={idx} className="pl-3 text-xs text-gray-600 relative" data-testid={`kpi-history-item-${kpi.id}-${idx}`}>
                                  <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-blue-400"></span>
                                  <span className="font-semibold text-gray-900">{h.from ?? 0} → {h.to ?? 0}</span>
                                  <span className="ml-1">{kpi.unit}</span>
                                  <span className="ml-2 text-gray-500">
                                    by <span className="font-medium">{h.by || 'unknown'}</span>
                                    {h.approved_by && h.approved_by !== h.by ? <span> · approved by <span className="font-medium">{h.approved_by}</span></span> : null}
                                    {h.at ? <span> · {new Date(h.at).toLocaleString('en-GB')}</span> : null}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        
                        {/* Approval/Rejection Notes */}
                        {kpi.approval_notes && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm">
                            <p className="font-medium text-green-900">{t('project_forms.pm_approval_notes')}</p>
                            <p className="text-gray-700">{kpi.approval_notes}</p>
                          </div>
                        )}
                        
                        {kpi.rejection_notes && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm">
                            <p className="font-medium text-red-900">{t('project_forms.pm_rejection_notes')}</p>
                            <p className="text-gray-700">{kpi.rejection_notes}</p>
                          </div>
                        )}
                        
                        {/* Comments for individual KPI */}
                        <div className="mt-4 pt-4 border-t">
                          <UniversalComments 
                            projectId={id} 
                            user={user} 
                            itemType="kpi" 
                            itemId={kpi.id}
                            title={t('project_forms.comments_for_item', { name: kpi.name })} 
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {kpis.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('project_forms.no_kpis_yet')}</p>
                    <p className="text-sm">{t('project_forms.no_kpis_hint')}</p>
                  </div>
                )}
              </div>
              
              {/* Universal Comments for KPIs */}
              <UniversalComments 
                projectId={id} 
                user={user} 
                itemType="kpis" 
                title={t('project_forms.kpis_comments_title')} 
              />
            </TabsContent>

            {/* Deliverables Tab */}
            <TabsContent value="deliverables" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">{t('project_forms.deliverables_section_title')}</h2>
                <Dialog open={showDeliverableDialog} onOpenChange={setShowDeliverableDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-deliverable-button">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('project_forms.add_deliverable_btn')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingDeliverable ? t('project_forms.deliverable_edit_title') : t('project_forms.deliverable_add_title')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateDeliverable} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="deliverable-name">{t('project_forms.deliverable_name')}</Label>
                        <Input
                          id="deliverable-name"
                          value={deliverableFormData.name}
                          onChange={(e) => setDeliverableFormData({...deliverableFormData, name: e.target.value})}
                          placeholder={t('project_forms.deliverable_name_placeholder')}
                          required
                          data-testid="deliverable-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="deliverable-description">{t('project_forms.deliverable_description')}</Label>
                        <Textarea
                          id="deliverable-description"
                          value={deliverableFormData.description}
                          onChange={(e) => setDeliverableFormData({...deliverableFormData, description: e.target.value})}
                          placeholder={t('project_forms.deliverable_description_placeholder')}
                          rows={3}
                          required
                          data-testid="deliverable-description-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="due-date">{t('project_forms.deliverable_due_date')}</Label>
                        <Input
                          id="due-date"
                          type="date"
                          value={deliverableFormData.due_date}
                          onChange={(e) => setDeliverableFormData({...deliverableFormData, due_date: e.target.value})}
                          required
                          data-testid="deliverable-due-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="deliverable-files">{t('project_forms.deliverable_files_label')}</Label>
                        
                        {/* Show existing files when editing */}
                        {editingDeliverable && deliverableFormData.existing_files && deliverableFormData.existing_files.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-600 mb-2">{t('project_forms.deliverable_existing_files')}</p>
                            <div className="space-y-1">
                              {deliverableFormData.existing_files.map((filename, index) => (
                                <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded text-sm">
                                  <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    <span>{filename}</span>
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(
                                          `${API}/projects/${id}/deliverables/${editingDeliverable}/files/${filename}`,
                                          {
                                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                            responseType: 'blob'
                                          }
                                        );
                                        
                                        // Create blob link to download
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', filename);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                      } catch (error) {
                                        console.error('Download failed:', error);
                                        toast.error('Failed to download file');
                                      }
                                    }}
                                    className="text-blue-700 hover:text-blue-800"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <Input
                          id="deliverable-files"
                          type="file"
                          multiple
                          onChange={(e) => setDeliverableFormData({...deliverableFormData, files: Array.from(e.target.files)})}
                          data-testid="deliverable-files-input"
                          className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {deliverableFormData.files.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600 mb-2">{t('project_forms.deliverable_selected_files')}</p>
                            <div className="space-y-1">
                              {deliverableFormData.files.map((file, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4 text-gray-500" />
                                    <span>{file.name}</span>
                                    <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newFiles = deliverableFormData.files.filter((_, i) => i !== index);
                                      setDeliverableFormData({...deliverableFormData, files: newFiles});
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <XIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowDeliverableDialog(false);
                          setEditingDeliverable(null);
                          setDeliverableFormData({
                            name: '',
                            description: '',
                            due_date: '',
                            files: []
                          });
                        }}>
                          {t('project_forms.cancel')}
                        </Button>
                        <Button type="submit" data-testid="create-deliverable-submit">{editingDeliverable ? t('project_forms.deliverable_update_btn') : t('project_forms.deliverable_create_btn')}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>



              {/* Bulk Actions Toolbar for Deliverables */}
              {(user.role === 'project_manager' || user.role === 'administrator') && 
               deliverables.some(d => d.edit_status === 'pending_approval') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <p className="text-sm font-medium text-blue-900">
                        {t('project_forms.deliverable_pending_count', { count: deliverables.filter(d => d.edit_status === 'pending_approval').length })}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const pendingDeliverables = deliverables.filter(d => 
                            d.edit_status === 'pending_approval' || d.approval_status === 'pending_approval'
                          ).map(d => d.id);
                          setSelectedDeliverables(pendingDeliverables);
                        }}
                      >
                        {t('project_forms.select_all_pending')}
                      </Button>
                      {selectedDeliverables.length > 0 && (
                        <span className="text-sm text-blue-700">
                          {t('project_forms.count_selected', { count: selectedDeliverables.length })}
                        </span>
                      )}
                    </div>
                    {selectedDeliverables.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkApprovalType('deliverables');
                            setShowBulkApprovalDialog(true);
                          }}
                          className="bg-green-700 hover:bg-green-800"
                        >
                          {t('project_forms.bulk_approve_label', { count: selectedDeliverables.length })}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setBulkApprovalType('deliverables');
                            setShowBulkApprovalDialog(true);
                          }}
                        >
                          {t('project_forms.bulk_reject_label', { count: selectedDeliverables.length })}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedDeliverables([])}
                        >
                          {t('project_forms.clear_btn')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {deliverables.map((deliverable) => (
                  <Card key={deliverable.id} data-testid={`deliverable-card-${deliverable.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {/* Checkbox for bulk selection */}
                            {(user.role === 'project_manager' || user.role === 'administrator') && 
                             (deliverable.edit_status === 'pending_approval' || deliverable.approval_status === 'pending_approval') && (
                              <input
                                type="checkbox"
                                checked={selectedDeliverables.includes(deliverable.id)}
                                onChange={() => toggleDeliverableSelection(deliverable.id)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                            )}
                            <Package className="h-5 w-5 text-gray-600" />
                            <h3 className="font-semibold text-lg">{deliverable.name}</h3>
                            <Badge className={getDeliverableStatusColor(deliverable.status)}>
                              {deliverable.status.replace('_', ' ')}
                            </Badge>
                            {deliverable.certification_status && (
                              <Badge className="bg-purple-100 text-purple-800">
                                <Award className="h-3 w-3 mr-1" />
                                {t('project_forms.certified_badge')}
                              </Badge>
                            )}
                            {/* Approval Status Badge */}
                            {(deliverable.edit_status === 'pending_approval' || deliverable.approval_status === 'pending_approval') && (
                              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                {t('project_forms.pending_approval_badge')}
                              </Badge>
                            )}
                            {deliverable.edit_status === 'rejected' && (
                              <Badge variant="destructive">
                                {t('project_forms.edit_rejected_badge')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-600 mb-3">{deliverable.description}</p>
                          
                          {/* Show approval notes if approved */}
                          {deliverable.approval_notes && deliverable.approval_status === 'approved' && (
                            <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                              <p className="text-sm font-medium text-green-800 mb-1">{t('project_forms.approved_by_prefix', { user: deliverable.approved_by })}</p>
                              <p className="text-sm text-green-700">{deliverable.approval_notes}</p>
                            </div>
                          )}
                          
                          {/* Show rejection notes if rejected */}
                          {deliverable.rejection_notes && deliverable.approval_status === 'rejected' && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                              <p className="text-sm font-medium text-red-800 mb-1">{t('project_forms.rejected_by_prefix', { user: deliverable.rejected_by })}</p>
                              <p className="text-sm text-red-700">{deliverable.rejection_notes}</p>
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-4 w-4" />
                              <span>{t('project_forms.due_label', { date: new Date(deliverable.due_date).toLocaleDateString('en-GB') })}</span>
                            </div>
                            {new Date(deliverable.due_date) < new Date() && deliverable.status !== 'completed' && deliverable.status !== 'certified' && (
                              <Badge className="bg-red-100 text-red-800">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {t('project_forms.overdue_badge')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex items-center space-x-2">
                          {deliverable.status === 'completed' ? (
                            <CheckCircle2 className="h-8 w-8 text-green-500" />
                          ) : deliverable.status === 'certified' ? (
                            <Award className="h-8 w-8 text-purple-500" />
                          ) : deliverable.status === 'in_progress' ? (
                            <Clock className="h-8 w-8 text-blue-500" />
                          ) : (
                            <AlertCircle className="h-8 w-8 text-gray-500" />
                          )}
                          
                          {/* Review Button for PM/Admin on Pending Approvals */}
                          {(user.role === 'project_manager' || user.role === 'administrator') && 
                           (deliverable.edit_status === 'pending_approval' || deliverable.approval_status === 'pending_approval') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setApprovingDeliverable(deliverable);
                                setShowDeliverableApprovalDialog(true);
                              }}
                              className="ml-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                            >
                              {t('project_forms.review_btn')}
                            </Button>
                          )}
                          
                          {/* Edit/Delete Actions for Admin and Social Actor */}
                          {user.role !== 'investor' && (
                            <div className="flex items-center space-x-1 ml-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setDeliverableFormData({
                                    name: deliverable.name,
                                    description: deliverable.description,
                                    due_date: (deliverable.due_date || new Date().toISOString()).split('T')[0],
                                    priority: deliverable.priority || 'medium',
                                    files: [],
                                    existing_files: deliverable.evidence_files || []
                                  });
                                  setEditingDeliverable(deliverable.id);
                                  setShowDeliverableDialog(true);
                                }}
                                className="text-blue-700 hover:text-blue-800"
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDeliverable(deliverable.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Display uploaded files */}
                      {deliverable.evidence_files && deliverable.evidence_files.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <FileText className="h-4 w-4 mr-1" />
                            {t('project_forms.uploaded_files_count', { count: deliverable.evidence_files.length })}
                          </h4>
                          <div className="space-y-2">
                            {deliverable.evidence_files.map((filename, index) => (
                              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm text-gray-700">{filename}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    try {
                                      const response = await axios.get(
                                        `${API}/projects/${id}/deliverables/${deliverable.id}/files/${filename}`,
                                        {
                                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                          responseType: 'blob'
                                        }
                                      );
                                      
                                      // Create blob link to download
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', filename);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.parentNode.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      console.error('Download failed:', error);
                                      toast.error('Failed to download file');
                                    }
                                  }}
                                  className="text-blue-700 hover:text-blue-800"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Comments for individual deliverable */}
                      <div className="mt-4 pt-4 border-t">
                        <UniversalComments 
                          projectId={id} 
                          user={user} 
                          itemType="deliverable" 
                          itemId={deliverable.id}
                          title={t('project_forms.comments_for_item', { name: deliverable.name })} 
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {deliverables.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('project_forms.no_deliverables_yet')}</p>
                    <p className="text-sm">{t('project_forms.no_deliverables_hint')}</p>
                  </div>
                )}
              </div>
              
              {/* Universal Comments for Deliverables */}
              <UniversalComments 
                projectId={id} 
                user={user} 
                itemType="deliverables" 
                title={t('project_forms.deliverables_comments_title')} 
              />
            </TabsContent>

            {/* Risks & Issues Tab */}
            <TabsContent value="risks" className="space-y-6">
              <RiskIssueManagement projectId={id} user={user} />
            </TabsContent>

            {/* Impact & SROI Tab */}
            <TabsContent value="impact" className="space-y-6">
              <SROICalculator projectId={id} user={user} />
            </TabsContent>

            {/* Document Management Tab removed - file uploads moved to deliverables */}

            {/* Phase Gate Management Tab */}
            {/* Gantt Chart & Timeline Management Tab */}
            <TabsContent value="timeline" className="space-y-6">
              <GanttChart projectId={id} user={user} />
            </TabsContent>

            {/* Financial Management Suite Tab */}
            <TabsContent value="financial" className="space-y-6">
              <FinancialManagement projectId={id} user={user} />
            </TabsContent>

            {/* Funding Ledger (Investor commitments + drawdowns) */}
            <TabsContent value="funding" className="space-y-6">
              <FundingLedger projectId={id} currentUser={user} />
            </TabsContent>

            {/* Social-Actor audit log (admin/PM see all; SA sees own) */}
            <TabsContent value="audit" className="space-y-6">
              <SocialActorAuditLog projectId={id} currentUser={user} />
            </TabsContent>


            {/* Beneficiary Tracking Module Tab */}
            <TabsContent value="beneficiaries" className="space-y-6">
              <BeneficiaryTracking projectId={id} user={user} />
            </TabsContent>

            {/* Compliance & Audit Module Tab */}
            <TabsContent value="compliance" className="space-y-6">
              <ComplianceAudit projectId={id} user={user} />
            </TabsContent>

            {/* Video Verification System Tab */}
            <TabsContent value="videos" className="space-y-6">
              <VideoVerification projectId={id} user={user} />
            </TabsContent>

            {/* Mobile Field Application Tab */}
            <TabsContent value="mobile" className="space-y-6">
              <MobileFieldApp projectId={id} user={user} />
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-6">
              <NotificationCenter user={user} />
            </TabsContent>

            {/* Quality Assurance Module Tab */}
            <TabsContent value="quality" className="space-y-6">
              <QualityAssuranceModule projectId={id} user={user} />
            </TabsContent>

            {/* Advanced Analytics & AI Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <ErrorBoundary label="Smart Alerts">
                <SmartAlerts projectId={id} user={user} />
              </ErrorBoundary>
              <ErrorBoundary label="Advanced Search">
                <AdvancedSearch projectId={id} user={user} />
              </ErrorBoundary>
              <ErrorBoundary label="Analytics Dashboard">
                <AnalyticsDashboard projectId={id} user={user} />
              </ErrorBoundary>
              <ErrorBoundary label="AI Insights">
                <AdvancedAnalyticsAI projectId={id} user={user} />
              </ErrorBoundary>
            </TabsContent>

            {/* Procurement Management Tab */}
            <TabsContent value="procurement" className="space-y-6">
              <ProcurementManagement projectId={id} user={user} />
            </TabsContent>

            {/* Advanced Reporting Engine Tab */}
            <TabsContent value="reports" className="space-y-6">
              {/* PDF Report Generation Section */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('project_forms.generate_pdf_title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {t('project_forms.generate_pdf_intro')}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button
                      onClick={() => handleGeneratePDFReport('comprehensive')}
                      className="flex flex-col items-center justify-center h-32 bg-blue-50 hover:bg-blue-100 text-blue-700 border-2 border-blue-200"
                      variant="outline"
                    >
                      <FileText className="h-8 w-8 mb-2" />
                      <span className="font-semibold">{t('project_forms.report_comprehensive')}</span>
                      <span className="text-xs text-gray-600">{t('project_forms.report_comprehensive_desc')}</span>
                    </Button>
                    <Button
                      onClick={() => handleGeneratePDFReport('financial')}
                      className="flex flex-col items-center justify-center h-32 bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-200"
                      variant="outline"
                    >
                      <DollarSign className="h-8 w-8 mb-2" />
                      <span className="font-semibold">{t('project_forms.report_financial')}</span>
                      <span className="text-xs text-gray-600">{t('project_forms.report_financial_desc')}</span>
                    </Button>
                    <Button
                      onClick={() => handleGeneratePDFReport('kpis')}
                      className="flex flex-col items-center justify-center h-32 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200"
                      variant="outline"
                    >
                      <TrendingUp className="h-8 w-8 mb-2" />
                      <span className="font-semibold">{t('project_forms.report_kpi')}</span>
                      <span className="text-xs text-gray-600">{t('project_forms.report_kpi_desc')}</span>
                    </Button>
                    <Button
                      onClick={() => handleGeneratePDFReport('beneficiaries')}
                      className="flex flex-col items-center justify-center h-32 bg-orange-50 hover:bg-orange-100 text-orange-700 border-2 border-orange-200"
                      variant="outline"
                    >
                      <Users className="h-8 w-8 mb-2" />
                      <span className="font-semibold">{t('project_forms.report_impact')}</span>
                      <span className="text-xs text-gray-600">{t('project_forms.report_impact_desc')}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <AdvancedReporting projectId={id} user={user} />
            </TabsContent>

            {/* Activity Feed Tab */}
            <TabsContent value="activity" className="space-y-6">
              <ActivityFeed projectId={id} user={user} />
            </TabsContent>

            {/* Team Management Tab */}
            <TabsContent value="collaboration" className="space-y-6">
              <TeamManagement projectId={id} user={user} />
            </TabsContent>

            {/* Digital Checklists Tab */}
            <TabsContent value="checklists" className="space-y-6">
              <DigitalChecklists projectId={id} user={user} />
            </TabsContent>

            {/* Document Evidence Management Tab */}
            <TabsContent value="documents" className="space-y-6">
              <DocumentEvidenceManagement projectId={id} user={user} />
            </TabsContent>
          </Tabs>
        </main>

        {/* KPI Approval Dialog */}
        <Dialog open={showKpiApprovalDialog} onOpenChange={setShowKpiApprovalDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('project_forms.review_kpi_title')}</DialogTitle>
            </DialogHeader>
            {approvingKpi && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">{t('project_forms.kpi_label')}: {approvingKpi.name}</h3>
                  {approvingKpi.pending_changes && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">{t('project_forms.proposed_changes')}</p>
                      <div className="bg-white border rounded-lg divide-y">
                        {Object.entries(approvingKpi.pending_changes).map(([field, newValue]) => {
                          // Skip internal fields
                          if (field === 'updated_at' || field === 'updated_by') return null;
                          
                          const oldValue = approvingKpi[field];
                          const fieldLabels = {
                            baseline_value: t('project_forms.field_baseline_value'),
                            target_value: t('project_forms.field_target_value'),
                            actual_value: t('project_forms.field_actual_value'),
                            name: t('project_forms.field_name'),
                            description: t('project_forms.field_description'),
                            unit: t('project_forms.field_unit'),
                            type: t('project_forms.field_type')
                          };
                          
                          const label = fieldLabels[field] || field;
                          
                          // Only show changed fields
                          if (oldValue === newValue) return null;
                          
                          return (
                            <div key={field} className="p-3">
                              <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
                              <div className="flex items-center space-x-2 text-sm">
                                <span className="text-red-600 line-through">{oldValue || 'N/A'}</span>
                                <span className="text-gray-500">→</span>
                                <span className="text-green-700 font-medium">{newValue || 'N/A'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t('project_forms.review_notes_label')}</Label>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder={t('project_forms.review_notes_placeholder')}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowKpiApprovalDialog(false);
                      setApprovingKpi(null);
                      setApprovalNotes('');
                    }}
                  >
                    {t('project_forms.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleKpiApproval(false)}
                  >
                    {t('project_forms.reject')}
                  </Button>
                  <Button
                    onClick={() => handleKpiApproval(true)}
                    className="bg-green-700 hover:bg-green-800"
                  >
                    {t('project_forms.approve')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Deliverable Approval Dialog */}
        <Dialog open={showDeliverableApprovalDialog} onOpenChange={setShowDeliverableApprovalDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('project_forms.review_deliverable_title')}</DialogTitle>
            </DialogHeader>
            {approvingDeliverable && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">{t('project_forms.deliverable_label')}: {approvingDeliverable.name}</h3>
                  
                  {/* Show for newly created deliverables (approval_status) */}
                  {approvingDeliverable.approval_status === 'pending_approval' && !approvingDeliverable.pending_changes && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">{t('project_forms.new_deliverable_details')}</p>
                      <div className="bg-white border rounded-lg divide-y">
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">{t('project_forms.field_description')}</p>
                          <p className="text-sm text-gray-600">{approvingDeliverable.description}</p>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">{t('project_forms.field_due_date')}</p>
                          <p className="text-sm text-gray-600">{new Date(approvingDeliverable.due_date).toLocaleDateString('en-GB')}</p>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">{t('project_forms.field_status')}</p>
                          <p className="text-sm text-gray-600">{approvingDeliverable.status}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Show for edited deliverables (edit_status with pending_changes) */}
                  {approvingDeliverable.pending_changes && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">{t('project_forms.proposed_changes')}</p>
                      <div className="bg-white border rounded-lg divide-y">
                        {Object.entries(approvingDeliverable.pending_changes).map(([field, newValue]) => {
                          // Skip internal fields
                          if (field === 'updated_at' || field === 'updated_by') return null;
                          
                          const oldValue = approvingDeliverable[field];
                          const fieldLabels = {
                            name: t('project_forms.field_name'),
                            description: t('project_forms.field_description'),
                            due_date: t('project_forms.field_due_date'),
                            status: t('project_forms.field_status'),
                            certification_status: t('project_forms.field_certification_status')
                          };
                          
                          const label = fieldLabels[field] || field;
                          
                          // Only show changed fields
                          if (oldValue === newValue) return null;
                          
                          // Format datetime values
                          let displayOldValue = oldValue;
                          let displayNewValue = newValue;
                          if (field === 'due_date') {
                            if (typeof displayOldValue === 'string') {
                              displayOldValue = displayOldValue.split('T')[0];
                            }
                            if (typeof displayNewValue === 'string') {
                              displayNewValue = displayNewValue.split('T')[0];
                            }
                          }
                          
                          return (
                            <div key={field} className="p-3">
                              <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
                              <div className="flex items-center space-x-2 text-sm">
                                <span className="text-red-600 line-through">{displayOldValue || 'N/A'}</span>
                                <span className="text-gray-500">→</span>
                                <span className="text-green-700 font-medium">{displayNewValue || 'N/A'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t('project_forms.review_notes_label')}</Label>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder={t('project_forms.review_notes_placeholder')}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeliverableApprovalDialog(false);
                      setApprovingDeliverable(null);
                      setApprovalNotes('');
                    }}
                  >
                    {t('project_forms.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeliverableApproval(false)}
                  >
                    {t('project_forms.reject')}
                  </Button>
                  <Button
                    onClick={() => handleDeliverableApproval(true)}
                    className="bg-green-700 hover:bg-green-800"
                  >
                    {t('project_forms.approve')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* Bulk Approval Dialog */}
        <Dialog open={showBulkApprovalDialog} onOpenChange={setShowBulkApprovalDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('project_forms.bulk_approval_title', { type: bulkApprovalType })}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  {t('project_forms.bulk_about_to_process', { count: bulkApprovalType === 'kpis' ? selectedKpis.length : selectedDeliverables.length })}
                </p>
                <p className="text-sm text-blue-700">
                  {t('project_forms.bulk_choice_hint')}
                </p>
              </div>
              <div>
                <Label>{t('project_forms.common_review_notes_optional')}</Label>
                <Textarea
                  value={bulkApprovalNotes}
                  onChange={(e) => setBulkApprovalNotes(e.target.value)}
                  placeholder={t('project_forms.common_review_notes_placeholder')}
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkApprovalDialog(false);
                    setBulkApprovalNotes('');
                  }}
                >
                  {t('project_forms.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleBulkApproval(false)}
                >
                  {t('project_forms.reject_all')}
                </Button>
                <Button
                  onClick={() => handleBulkApproval(true)}
                  className="bg-green-700 hover:bg-green-800"
                >
                  {t('project_forms.approve_all')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


      </div>
    </div>
  );
};

export default ProjectDetails;